import type {
  StreamedCardsListData,
  StreamedCardsListOption,
  StreamedCardsListUiComponentData,
  StreamedCarouselData,
  StreamedCarouselOfferId,
  StreamedCarouselUiComponentData,
  StreamedPillsListData,
  StreamedPillsListOption,
  StreamedPillsListUiComponentData,
  StreamedProgressiveUiComponentData,
  StreamedProgressiveUiData,
  StreamedTestimonialData,
  StreamedTestimonialReview,
  StreamedTestimonialUiComponentData,
} from 'tripPlanner/api/types'
import { isRecord } from 'lib/object/objectUtils'

export function isStreamedCardOption(value: unknown): value is StreamedCardsListOption {
  if (!isRecord(value)) return false
  return typeof value.title === 'string' &&
    (value.description === undefined || typeof value.description === 'string') &&
    (value.emoji === undefined || typeof value.emoji === 'string') &&
    (value.placeSearch === undefined || typeof value.placeSearch === 'string') &&
    (value.action === undefined || typeof value.action === 'string')
}

export function isStreamedCardsListData(value: unknown): value is StreamedCardsListData {
  if (!isRecord(value)) return false
  return value.type === 'cards_list' &&
    (value.select === 'single' || value.select === 'multiple') &&
    Array.isArray(value.options) &&
    value.options.every(isStreamedCardOption) &&
    (value.categories === undefined || value.categories === 'generic' || value.categories === 'places' || value.categories === 'locations')
}

export function isStreamedPillOption(value: unknown): value is StreamedPillsListOption {
  if (!isRecord(value)) return false
  return typeof value.label === 'string' &&
    (value.action === undefined || typeof value.action === 'string')
}

export function isStreamedPillsListData(value: unknown): value is StreamedPillsListData {
  if (!isRecord(value)) return false
  return value.type === 'pills_list' &&
    (value.select === 'single' || value.select === 'multiple') &&
    Array.isArray(value.options) &&
    value.options.every(isStreamedPillOption)
}

export function isStreamedTestimonialReview(value: unknown): value is StreamedTestimonialReview {
  if (!isRecord(value)) return false
  return typeof value.reviewerName === 'string' &&
    typeof value.text === 'string' &&
    (value.rating === undefined || (typeof value.rating === 'number' && Number.isFinite(value.rating) && value.rating >= 0)) &&
    (value.date === undefined || typeof value.date === 'string')
}

export function isStreamedTestimonialData(value: unknown): value is StreamedTestimonialData {
  if (!isRecord(value)) return false
  return value.type === 'testimonial' &&
    Array.isArray(value.reviews) &&
    value.reviews.every(isStreamedTestimonialReview)
}

export function isStreamedCarouselOfferId(value: unknown): value is StreamedCarouselOfferId {
  return typeof value === 'string'
}

export function isStreamedCarouselOccupancy(value: unknown): value is NonNullable<StreamedCarouselData['occupancy']> {
  if (!Array.isArray(value) || value.length === 0) return false
  return value.every((room) => {
    if (!isRecord(room)) return false
    return typeof room.adults === 'number' &&
      Number.isFinite(room.adults) &&
      (room.children === undefined || (typeof room.children === 'number' && Number.isFinite(room.children))) &&
      (room.childrenAge === undefined || (Array.isArray(room.childrenAge) && room.childrenAge.every((age) => typeof age === 'number' && Number.isFinite(age))))
  })
}

export function isStreamedCarouselData(value: unknown): value is StreamedCarouselData {
  if (!isRecord(value)) return false
  return value.type === 'carousel' &&
    (value.product === 'hotel' || value.product === 'experience' || value.product === 'cruise') &&
    Array.isArray(value.offerIds) &&
    value.offerIds.every(isStreamedCarouselOfferId) &&
    (value.title === undefined || typeof value.title === 'string') &&
    (value.occupancy === undefined || value.occupancy === null || isStreamedCarouselOccupancy(value.occupancy))
}

export function isStreamedProgressiveUiData(value: unknown): value is StreamedProgressiveUiData {
  return isStreamedCardsListData(value) ||
    isStreamedPillsListData(value) ||
    isStreamedTestimonialData(value) ||
    isStreamedCarouselData(value)
}

export function isStreamedCardsListUiComponentData(value: unknown): value is StreamedCardsListUiComponentData {
  if (!isRecord(value)) return false
  const { streamedPartId, streamingStatus } = value
  return isStreamedCardsListData(value) &&
    typeof streamedPartId === 'string' &&
    (streamingStatus === 'loading' || streamingStatus === 'streaming' || streamingStatus === 'complete')
}

export function isStreamedPillsListUiComponentData(value: unknown): value is StreamedPillsListUiComponentData {
  if (!isRecord(value)) return false
  const { streamedPartId, streamingStatus } = value
  return isStreamedPillsListData(value) &&
    typeof streamedPartId === 'string' &&
    (streamingStatus === 'loading' || streamingStatus === 'streaming' || streamingStatus === 'complete')
}

export function isStreamedTestimonialUiComponentData(value: unknown): value is StreamedTestimonialUiComponentData {
  if (!isRecord(value)) return false
  const { streamedPartId, streamingStatus } = value
  return isStreamedTestimonialData(value) &&
    typeof streamedPartId === 'string' &&
    (streamingStatus === 'loading' || streamingStatus === 'streaming' || streamingStatus === 'complete')
}

export function isStreamedCarouselUiComponentData(value: unknown): value is StreamedCarouselUiComponentData {
  if (!isRecord(value)) return false
  const { streamedPartId, streamingStatus } = value
  return isStreamedCarouselData(value) &&
    typeof streamedPartId === 'string' &&
    (streamingStatus === 'loading' || streamingStatus === 'streaming' || streamingStatus === 'complete')
}

export function isStreamedProgressiveUiComponentData(value: unknown): value is StreamedProgressiveUiComponentData {
  return isStreamedCardsListUiComponentData(value) ||
    isStreamedPillsListUiComponentData(value) ||
    isStreamedTestimonialUiComponentData(value) ||
    isStreamedCarouselUiComponentData(value)
}
